import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Inbox, UploadCloud, Mail, Search as SearchIcon, FileText, Image as ImageIcon,
  Tag, Loader2, AlertTriangle, CheckCircle2, RefreshCw, Filter, Layers,
  PartyPopper, FolderOpen, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "PLANS", "SPECIFICATIONS", "REPORTS", "UTILITIES", "PERMITS",
  "MOT", "CONTRACTS", "SUBMITTALS", "RFIS", "PHOTOS", "OTHER",
];

/* ============================================================
 * Iter EXP-3 — UploadStageTimeline
 *
 * Per-file pipeline visualization. Operator can WATCH each file
 * move through five stages instead of staring at a single
 * progress percentage. Each stage is a small pill that lights up
 * as the file enters it.
 *
 * Backend only reports "uploading" / "done" / "failed" via the
 * existing endpoint, so this client renders the first three
 * stages deterministically (uploading → done collapses through
 * Splitting + Tiling as the file moves server-side). When richer
 * status is wired into the API, set stage explicitly from the
 * per-file row.
 * ============================================================ */
function UploadStageTimeline({ status, progress }) {
  // Map current row status onto stage index.
  // queued → -1 (none lit); uploading <100 → 0; uploading 100 / done → 4.
  const stages = ["Uploading", "Processing", "Splitting", "Tiling", "Ready"];
  let active;
  if (status === "queued") active = -1;
  else if (status === "failed") active = -2;
  else if (status === "done") active = 4;
  else if (status === "uploading" && progress >= 95) active = 3;
  else if (status === "uploading" && progress >= 60) active = 1;
  else active = 0;

  const isFailed = status === "failed";
  return (
    <div className="ml-6 flex items-center gap-1.5" data-testid="upload-stage-timeline" data-active-stage={active}>
      {stages.map((label, i) => {
        const reached = active >= i;
        const isCurrent = active === i;
        const bg = isFailed && i === 0 ? "bg-red-500" : reached ? "bg-emerald-400" : "bg-[var(--fop-border-2)]";
        return (
          <div key={label} className="flex items-center gap-1.5 flex-shrink-0">
            <div
              className={`w-2 h-2 rounded-full ${bg} ${isCurrent && !isFailed ? "ring-2 ring-emerald-300/40 animate-pulse" : ""}`}
              aria-hidden="true"
            />
            <span className={`text-[9px] font-mono uppercase tracking-wider ${reached ? "text-[var(--fop-text-2)]" : "text-[var(--fop-text-3)]"}`}>
              {label}
            </span>
            {i < stages.length - 1 && (
              <div className={`w-3 h-px ${reached ? "bg-emerald-400/50" : "bg-[var(--fop-border)]"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}


const CAT_COLOR = {
  PLANS: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  SPECIFICATIONS: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  REPORTS: "bg-purple-500/10 text-purple-300 border-purple-500/30",
  UTILITIES: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  PERMITS: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  MOT: "bg-red-500/10 text-red-300 border-red-500/30",
  CONTRACTS: "bg-slate-500/10 text-slate-300 border-slate-500/30",
  SUBMITTALS: "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30",
  RFIS: "bg-pink-500/10 text-pink-300 border-pink-500/30",
  PHOTOS: "bg-teal-500/10 text-teal-300 border-teal-500/30",
  OTHER: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
};

const OCR_BADGE = {
  pending: "text-zinc-400",
  processing: "text-amber-400",
  complete: "text-emerald-400",
  failed: "text-red-400",
  skipped: "text-zinc-500",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function IntakeCenter() {
  const { user } = useAuth();
  const canWrite = ["owner", "admin", "project_manager", "superintendent"].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [params, setParams] = useSearchParams();
  const [projectId, setProjectId] = useState(params.get("project") || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ category: "", source: "" });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Load projects on mount
  useEffect(() => {
    api.get("/projects").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setProjects(list);
      if (!projectId && list[0]) {
        setProjectId(list[0].id);
        const next = new URLSearchParams(params);
        next.set("project", list[0].id);
        setParams(next, { replace: true });
      }
    });
  /* eslint-disable-next-line */
  }, []);

  const loadQueue = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ limit: "200" });
      if (filter.category) q.set("category", filter.category);
      if (filter.source) q.set("source", filter.source);
      const r = await api.get(`/projects/${projectId}/intake-documents?${q.toString()}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load queue");
    } finally { setLoading(false); }
  }, [projectId, filter.category, filter.source]);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  const handleProjectChange = (id) => {
    setProjectId(id);
    const next = new URLSearchParams(params);
    next.set("project", id);
    setParams(next, { replace: true });
  };

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const q = (searchQ || "").trim();
    if (q.length < 2) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const params = new URLSearchParams({ q, limit_per_group: "15" });
      if (projectId) params.set("project_id", projectId);
      const r = await api.get(`/search/global?${params.toString()}`);
      setSearchResults(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed");
    } finally { setSearching(false); }
  };

  const overrideCategory = async (docId, cat) => {
    try {
      await api.post(`/intake-documents/${docId}/classify`, { category: cat });
      toast.success(`Set to ${cat}`);
      await loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override failed");
    }
  };

  const runOcr = async (docId) => {
    try {
      await api.post(`/intake-documents/${docId}/ocr`, {});
      toast.success("OCR complete");
      await loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OCR failed");
    }
  };

  // Iter 112 F-008 — Extract spec sections from a SPECIFICATIONS-tagged
  // intake document. Calls the existing backend Spec Splitter endpoint.
  const extractSpec = async (docId) => {
    const tid = toast.loading("Extracting spec sections…");
    try {
      const r = await api.post(`/intake-documents/${docId}/extract-spec-book`);
      const n = r?.data?.sections ?? 0;
      toast.success(n > 0 ? `Extracted ${n} spec section${n === 1 ? "" : "s"}` : "No spec sections detected", { id: tid });
      await loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Spec extraction failed", { id: tid });
    }
  };

  const canExtractSpec = ["owner", "admin", "project_manager", "project_engineer"].includes(user?.role);

  const totals = useMemo(() => {
    const t = {
      total: items.length,
      pending: items.filter((i) => i.status === "pending").length,
      email: items.filter((i) => i.intake_source === "email").length,
      ocrPending: items.filter((i) => ["pending", "processing"].includes(i.ocr_status)).length,
      lowConfidence: items.filter((i) => (i.classification_confidence || 0) < 0.5).length,
    };
    return t;
  }, [items]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="intake-center-page">
      <div className="fop-surface-2 p-5 rounded-lg mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">Project Documents</div>
        <h1 className="font-display text-2xl text-[var(--fop-text)] inline-flex items-center gap-2">
          <Inbox size={20} className="text-[var(--fop-primary)]" /> Documents Inbox
        </h1>
        <p className="text-xs text-[var(--fop-text-3)] mt-1 max-w-2xl">
          Auto-classified, OCR-indexed project documents from web upload + project upload-email.
        </p>
      </div>

      {/* Top controls */}
      <div className="fop-surface-2 rounded-lg p-3 mb-3 flex items-center gap-2 flex-wrap" data-testid="intake-controls">
        <select
          value={projectId}
          onChange={(e) => handleProjectChange(e.target.value)}
          className="px-2 py-1.5 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-xs min-w-[200px]"
          data-testid="intake-project-select"
        >
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button
          onClick={() => setUploadOpen(true)}
          disabled={!projectId || !canWrite}
          title={canWrite ? "Bulk upload" : "Upload requires PM, Super, Admin, or Owner"}
          className="btn btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="open-bulk-upload"
        >
          <UploadCloud size={12} /> Bulk upload
        </button>
        <div className="ml-auto inline-flex items-center gap-3 text-[10px] font-mono text-[var(--fop-text-3)]">
          <span data-testid="stat-total">{totals.total} docs</span>
          <span className="text-amber-400" data-testid="stat-email">{totals.email} via email</span>
          <span className="text-emerald-400" data-testid="stat-ocr">OCR queue · {totals.ocrPending}</span>
          <span className="text-red-400" data-testid="stat-lowconfidence">{totals.lowConfidence} low confidence</span>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={runSearch} className="fop-surface-2 rounded-lg p-3 mb-3 flex items-center gap-2" data-testid="search-bar">
        <SearchIcon size={14} className="text-[var(--fop-text-3)]" />
        <input
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder='Search across plans, intake docs, photos, observations… (e.g. "48 inch RCP")'
          data-testid="search-input"
          className="flex-1 px-2 py-1.5 bg-transparent text-sm text-[var(--fop-text)] outline-none"
        />
        <button type="submit" disabled={searching || (searchQ || "").trim().length < 2} className="btn btn-primary text-xs inline-flex items-center gap-1.5" data-testid="search-submit">
          {searching ? <Loader2 size={11} className="animate-spin" /> : <SearchIcon size={11} />} Search
        </button>
        {searchResults && (
          <button type="button" onClick={() => { setSearchResults(null); setSearchQ(""); }} className="btn btn-ghost text-xs" data-testid="search-clear">Clear</button>
        )}
      </form>

      {searchResults ? (
        <SearchResults data={searchResults} />
      ) : (
        <>
          {/* Email card */}
          {projectId && (
            <div className="mb-3" data-testid="intake-center-email-card">
              <EmailIntakeBlock projectId={projectId} />
            </div>
          )}

          {/* Filter strip */}
          <div className="fop-surface-2 rounded-lg p-3 mb-3 flex items-center gap-2 flex-wrap" data-testid="queue-filters">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
              <Filter size={11} /> Filter
            </div>
            <select
              value={filter.category}
              onChange={(e) => setFilter({ ...filter, category: e.target.value })}
              data-testid="filter-category"
              className="px-2 py-1 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-xs"
            >
              <option value="">All categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={filter.source}
              onChange={(e) => setFilter({ ...filter, source: e.target.value })}
              data-testid="filter-source"
              className="px-2 py-1 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-xs"
            >
              <option value="">All sources</option>
              <option value="upload">Web upload</option>
              <option value="email">Email</option>
            </select>
            <button onClick={loadQueue} className="btn btn-ghost text-[11px] inline-flex items-center gap-1" data-testid="reload-queue">
              <RefreshCw size={11} /> Reload
            </button>
          </div>

          {/* Queue */}
          {loading ? (
            <div className="p-8 font-mono text-xs text-[var(--fop-text-3)] flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading queue…
            </div>
          ) : items.length === 0 ? (
            <div className="fop-surface-2 p-8 rounded-lg text-center text-sm text-[var(--fop-text-3)]" data-testid="queue-empty">
              No intake documents yet. Drag files into Bulk Upload, or email them to the project address.
            </div>
          ) : (
            <div className="space-y-2" data-testid="queue-list">
              {items.map((it) => (
                <IntakeRow
                  key={it.id}
                  item={it}
                  onOverride={overrideCategory}
                  onOcr={runOcr}
                  onExtractSpec={canExtractSpec ? extractSpec : null}
                />
              ))}
            </div>
          )}
        </>
      )}

      {uploadOpen && (
        <BulkUploadModal
          projectId={projectId}
          onClose={() => setUploadOpen(false)}
          onDone={() => { setUploadOpen(false); loadQueue(); }}
        />
      )}
    </div>
  );
}

function EmailIntakeBlock({ projectId }) {
  const { user } = useAuth();
  const canRegen = ["owner", "admin"].includes(user?.role);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}/upload-email`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [projectId]);
  if (!data) return null;
  return (
    <div className="fop-surface-2 rounded-lg p-3 flex items-center gap-3" data-testid="intake-email-block">
      <Mail size={14} className="text-[var(--fop-primary)]" />
      <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">Project upload email</span>
      <code className="font-mono text-xs text-[var(--fop-text)] flex-1 truncate" data-testid="ic-upload-email">{data.address}</code>
      <button onClick={() => { navigator.clipboard.writeText(data.address); toast.success("Copied"); }} className="btn btn-ghost text-[11px]" data-testid="ic-copy-email">Copy</button>
    </div>
  );
}

function IntakeRow({ item, onOverride, onOcr, onExtractSpec }) {
  const [openOverride, setOpenOverride] = useState(false);
  const isSpec = item.intake_category === "SPECIFICATIONS";
  const ocrReady = item.ocr_status === "complete";
  return (
    <div className="fop-surface-2 rounded-lg p-3 border border-[var(--fop-border)] hover:border-[var(--fop-border-hi)] transition-colors" data-testid={`intake-row-${item.id}`}>
      <div className="flex items-start gap-3">
        {item.intake_category === "PHOTOS" ? <ImageIcon size={14} className="text-[var(--fop-text-3)] mt-1" /> : <FileText size={14} className="text-[var(--fop-text-3)] mt-1" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded border ${CAT_COLOR[item.intake_category] || CAT_COLOR.OTHER}`} data-testid={`cat-${item.id}`}>
              {item.intake_category || "—"}
            </span>
            <span className="text-[10px] font-mono text-[var(--fop-text-4)]">
              conf {Math.round((item.classification_confidence || 0) * 100)}%
              {item.classification_overridden && " · override"}
            </span>
            <span className={`text-[10px] font-mono ${OCR_BADGE[item.ocr_status] || ""}`} data-testid={`ocr-${item.id}`}>
              OCR · {item.ocr_status}
            </span>
            <span className="text-[10px] font-mono text-[var(--fop-text-4)]">
              · {item.intake_source === "email" ? "email" : "upload"}
            </span>
          </div>
          <div className="font-medium text-sm text-[var(--fop-text)] mt-1 truncate">{item.file_name}</div>
          {item.intake_source === "email" && item.intake_email_from && (
            <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-0.5">
              from {item.intake_email_from}{item.intake_email_subject ? ` · "${item.intake_email_subject}"` : ""}
            </div>
          )}
          <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-1">
            {Math.round((item.file_size || 0) / 1024)} KB · {fmtDate(item.uploaded_at)}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <button onClick={() => setOpenOverride(!openOverride)} className="btn btn-ghost text-[11px] inline-flex items-center gap-1" data-testid={`override-${item.id}`}>
            <Tag size={11} /> Set category
          </button>
          {item.ocr_status !== "complete" && item.ocr_status !== "processing" && (
            <button onClick={() => onOcr(item.id)} className="btn btn-ghost text-[11px] inline-flex items-center gap-1" data-testid={`ocr-run-${item.id}`}>
              Run OCR
            </button>
          )}
          {/* Iter 112 F-008 — Spec extraction CTA, only on SPECIFICATIONS
              docs once OCR has completed; gated by canExtractSpec parent. */}
          {isSpec && onExtractSpec && ocrReady && (
            <button
              onClick={() => onExtractSpec(item.id)}
              className="btn btn-primary text-[11px] inline-flex items-center gap-1 px-2.5"
              data-testid={`extract-spec-${item.id}`}
              title="Split this spec book into searchable sections"
            >
              <Layers size={11} /> Extract spec sections
            </button>
          )}
        </div>
      </div>
      {openOverride && (
        <div className="mt-2 pl-7 flex items-center gap-1 flex-wrap" data-testid={`override-panel-${item.id}`}>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => { onOverride(item.id, c); setOpenOverride(false); }}
              className={`px-2 py-0.5 text-[10px] font-mono rounded border ${CAT_COLOR[c]}`}
              data-testid={`override-pick-${item.id}-${c}`}
            >{c}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function BulkUploadModal({ projectId, onClose, onDone }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [queue, setQueue] = useState([]); // [{file, status, progress, error}]
  const [busy, setBusy] = useState(false);
  // Iter EXP-4 — Victory state for full-screen success card.
  const [victory, setVictory] = useState(null);

  const addFiles = (files) => {
    const next = Array.from(files || []).map((f) => ({
      file: f, status: "queued", progress: 0, error: null, key: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 8)}`,
    }));
    setQueue((q) => [...q, ...next]);
  };

  const onDrop = (e) => { e.preventDefault(); addFiles(e.dataTransfer.files); };

  const uploadOne = async (entry) => {
    const fd = new FormData();
    fd.append("files", entry.file);
    setQueue((q) => q.map((x) => x.key === entry.key ? { ...x, status: "uploading", progress: 0 } : x));
    try {
      await api.post(`/projects/${projectId}/intake/bulk`, fd, {
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 95) : 50;
          setQueue((q) => q.map((x) => x.key === entry.key ? { ...x, progress: pct } : x));
        },
      });
      setQueue((q) => q.map((x) => x.key === entry.key ? { ...x, status: "done", progress: 100 } : x));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      setQueue((q) => q.map((x) => x.key === entry.key ? { ...x, status: "failed", error: msg } : x));
    }
  };

  const uploadAll = async () => {
    setBusy(true);
    const t0 = performance.now();
    const toRun = queue.filter((x) => x.status === "queued" || x.status === "failed");
    for (const e of toRun) {
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(e);
    }
    setBusy(false);
    const elapsed = Math.max(0, (performance.now() - t0) / 1000);
    // Iter EXP-4 — Upload VICTORY MOMENT.
    // Operator deserves more than a toast after pushing 250 sheets up
    // the wire. When the batch completes with zero failures, fire the
    // full-screen success card; toast on partial. Card shows: file
    // count, total MB, elapsed, and the three next-step CTAs.
    setQueue((q) => {
      const done = q.filter((x) => x.status === "done").length;
      const failed = q.filter((x) => x.status === "failed").length;
      const total = q.length;
      const totalMb = q.filter((x) => x.status === "done")
        .reduce((acc, x) => acc + (x.file?.size || 0), 0) / (1024 * 1024);
      if (total > 0 && failed === 0) {
        setVictory({
          files: done,
          mb: totalMb,
          elapsed_s: elapsed,
        });
      } else if (failed > 0) {
        toast.warning(`${done} of ${total} uploaded · ${failed} failed`, {
          description: "Tap Retry on each failed row to try again. Originals are still on your device.",
          duration: 7000,
        });
      }
      return q;
    });
  };

  const retryFailed = async () => {
    setBusy(true);
    const fails = queue.filter((x) => x.status === "failed");
    for (const e of fails) {
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(e);
    }
    setBusy(false);
  };

  const remove = (key) => setQueue((q) => q.filter((x) => x.key !== key));

  const counts = {
    total: queue.length,
    done: queue.filter((x) => x.status === "done").length,
    failed: queue.filter((x) => x.status === "failed").length,
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={busy || victory ? undefined : onClose}>
      {/* Iter EXP-4 — Victory full-screen card swaps in when batch
          completes cleanly. Replaces the toast-only feedback with a
          deliberate "mission accomplished" moment. */}
      {victory && (
        <UploadVictoryCard
          summary={victory}
          projectId={projectId}
          onClose={() => { setVictory(null); onDone && onDone(); }}
          onUploadMore={() => { setVictory(null); setQueue([]); }}
          onOpenSheets={() => { setVictory(null); navigate(`/sheets?project=${projectId}`); }}
          onOpenPlanRoom={() => { setVictory(null); navigate(`/project/${projectId}/plan-room`); }}
        />
      )}
      <div className="fop-surface-2 rounded-lg p-5 w-full max-w-2xl border border-[var(--fop-border-hi)] max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="bulk-upload-modal" style={{ display: victory ? "none" : undefined }}>
        <h3 className="font-display text-lg text-[var(--fop-text)] mb-1">Bulk Upload</h3>
        <p className="text-xs text-[var(--fop-text-3)] mb-3">Drag &amp; drop or pick multiple files. Each is validated and classified independently — failed files can be retried without restarting the batch.</p>

        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-[var(--fop-border-hi)] rounded-md p-6 text-center cursor-pointer hover:bg-[var(--fop-surface)] transition-colors mb-3"
          data-testid="bulk-dropzone"
        >
          <UploadCloud size={24} className="mx-auto text-[var(--fop-text-3)] mb-2" />
          <div className="text-sm text-[var(--fop-text-2)]">Drop files here or click to pick</div>
          <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-1">PDF · DOCX · XLSX · CSV · TXT · JPG · PNG</div>
          <input
            ref={inputRef}
            type="file"
            multiple
            onChange={(e) => addFiles(e.target.files)}
            className="hidden"
            accept=".pdf,.docx,.xlsx,.csv,.txt,.jpg,.jpeg,.png"
            data-testid="bulk-file-input"
          />
        </div>

        {queue.length > 0 && (
          <div className="flex-1 overflow-y-auto border border-[var(--fop-border)] rounded p-1 mb-3 max-h-72" data-testid="bulk-queue">
            <ul className="space-y-1">
              {queue.map((e) => (
                <li key={e.key} className="px-2 py-1.5 flex flex-col gap-1 text-[11px] font-mono" data-testid={`bulk-row-${e.key}`}>
                  <div className="flex items-center gap-2">
                    {e.status === "done" && <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" />}
                    {e.status === "failed" && <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />}
                    {e.status === "uploading" && <Loader2 size={14} className="animate-spin text-amber-400 flex-shrink-0" />}
                    {e.status === "queued" && <Layers size={14} className="text-[var(--fop-text-4)] flex-shrink-0" />}
                    <span className="flex-1 truncate text-[var(--fop-text)]">{e.file.name}</span>
                    <span className="text-[var(--fop-text-3)] text-[10px]">{Math.round(e.file.size / 1024)} KB</span>
                    {e.status === "uploading" && <span className="text-amber-400 text-[11px] font-bold">{e.progress}%</span>}
                    {e.status === "failed" && (
                      <button onClick={() => uploadOne(e)} className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:text-[var(--fop-primary-hover)]" data-testid={`bulk-retry-${e.key}`} title="Retry this file">
                        Retry
                      </button>
                    )}
                    {e.status === "queued" && (
                      <button onClick={() => remove(e.key)} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid={`bulk-remove-${e.key}`} aria-label="Remove">×</button>
                    )}
                  </div>
                  {/* Iter EXP-3 — Per-row pipeline timeline. Operator can
                      *watch* each file pass through the stages instead of
                      staring at a single progress %. Five stages match
                      backend: upload → process → split → tile → ready. */}
                  <UploadStageTimeline status={e.status} progress={e.progress || 0} />
                  {e.status === "failed" && e.error && (
                    <div className="ml-6 text-red-300 text-[10px] leading-tight" title={e.error}>
                      {e.error}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-mono text-[var(--fop-text-4)]">
            {counts.total} files · {counts.done} done · {counts.failed} failed
          </div>
          <div className="flex items-center gap-2">
            {counts.failed > 0 && (
              <button onClick={retryFailed} disabled={busy} className="btn btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="retry-failed">
                <RefreshCw size={11} /> Retry failed
              </button>
            )}
            <button onClick={onClose} disabled={busy} className="btn btn-ghost text-xs">Close</button>
            <button onClick={counts.done > 0 && counts.failed === 0 && counts.done === counts.total ? onDone : uploadAll} disabled={busy || queue.length === 0} className="btn btn-primary text-xs inline-flex items-center gap-1.5" data-testid="bulk-start">
              {busy ? <Loader2 size={11} className="animate-spin" /> : <UploadCloud size={11} />}
              {counts.done === counts.total && counts.total > 0 ? "Done" : `Upload ${queue.filter((x) => x.status !== "done").length}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SearchResults({ data }) {
  const groups = Object.entries(data?.groups || {}).filter(([_, v]) => v && v.length);
  if (data?.total === 0) {
    return (
      <div className="fop-surface-2 p-8 rounded-lg text-center text-sm text-[var(--fop-text-3)]" data-testid="search-empty">
        No matches for "{data.query}"
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="search-results">
      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
        {data.total} result{data.total === 1 ? "" : "s"} for "{data.query}"
      </div>
      {groups.map(([k, items]) => (
        <div key={k} className="fop-surface-2 rounded-lg p-4 border border-[var(--fop-border)]" data-testid={`search-group-${k}`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)] mb-2">
            {k} · {data.counts?.[k] || items.length}
          </div>
          <ul className="space-y-1">
            {items.map((it) => (
              <li key={it.id} className="text-[11px] font-mono text-[var(--fop-text-2)] truncate">
                {k === "plans" && (
                  <Link to={`/sheet/${it.id}`} className="hover:text-[var(--fop-primary)]" data-testid={`result-${it.id}`}>
                    <span className="text-[var(--fop-text)]">{it.sheet_number}</span> · {it.sheet_title} · {it.discipline}
                  </Link>
                )}
                {k === "intake" && (
                  <span data-testid={`result-${it.id}`}>
                    <span className="text-[var(--fop-text)]">{it.file_name}</span>
                    {it.intake_category && <span className="text-[var(--fop-text-4)]"> · {it.intake_category}</span>}
                    {it.intake_email_from && <span className="text-[var(--fop-text-4)]"> · from {it.intake_email_from}</span>}
                  </span>
                )}
                {k === "photos" && (
                  <span data-testid={`result-${it.id}`}>
                    <span className="text-[var(--fop-text)]">{it.caption || it.id}</span>
                  </span>
                )}
                {k === "observations" && (
                  <span data-testid={`result-${it.id}`}>
                    <span className="text-[var(--fop-text)]">{it.title || it.description?.slice(0, 80)}</span>
                  </span>
                )}
                {k === "daily_reports" && (
                  <span data-testid={`result-${it.id}`}>
                    <span className="text-[var(--fop-text)]">{it.report_date} · {(it.summary || "").slice(0, 80)}</span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}


/* ============================================================
 * Iter EXP-4 — Upload Victory Card
 *
 * Full-screen "mission accomplished" moment when a batch
 * uploads cleanly. Operator deserves more than a toast after
 * pushing 250 sheets up the wire.
 *
 * Three clear next-step CTAs: Open Sheets, Open Plan Room,
 * Upload More. No dead-ends. No silent finish.
 * ============================================================ */
function UploadVictoryCard({ summary, projectId, onClose, onUploadMore, onOpenSheets, onOpenPlanRoom }) {
  const { files = 0, mb = 0, elapsed_s = 0 } = summary || {};
  const fmtMb = mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(mb * 1024)} KB`;
  const fmtElapsed = elapsed_s >= 60
    ? `${Math.floor(elapsed_s / 60)}m ${Math.round(elapsed_s % 60)}s`
    : `${elapsed_s.toFixed(1)}s`;
  return (
    <div
      className="relative z-[60] w-full max-w-2xl rounded-2xl border-2 p-8 shadow-2xl"
      style={{
        background: "linear-gradient(180deg, rgba(34,197,94,0.10) 0%, var(--fop-surface) 60%)",
        borderColor: "var(--fop-success, #22c55e)",
      }}
      data-testid="upload-victory-card"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
             style={{ background: "var(--fop-success, #22c55e)" }}>
          <PartyPopper size={32} className="text-white" />
        </div>
        <div className="flex-1">
          <div className="font-display text-3xl font-bold text-[var(--fop-text)] leading-tight">
            Mission accomplished
          </div>
          <div className="text-sm text-[var(--fop-text-2)] mt-1">
            Your plans are uploaded and processing has started. Pages will appear
            in the queue as they're split and tiled.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-7" data-testid="victory-stats">
        <Stat label="Files Uploaded" value={files} />
        <Stat label="Total Size" value={fmtMb} />
        <Stat label="Elapsed" value={fmtElapsed} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <button
          type="button"
          onClick={onOpenSheets}
          className="btn btn-primary py-3 text-sm font-bold inline-flex items-center justify-center gap-2"
          data-testid="victory-open-sheets"
        >
          Open sheets <ArrowRight size={14} />
        </button>
        <button
          type="button"
          onClick={onOpenPlanRoom}
          className="btn btn-ghost py-3 text-sm font-bold inline-flex items-center justify-center gap-2"
          data-testid="victory-open-plan-room"
        >
          <FolderOpen size={14} /> Plan Room
        </button>
        <button
          type="button"
          onClick={onUploadMore}
          className="btn btn-ghost py-3 text-sm font-bold inline-flex items-center justify-center gap-2"
          data-testid="victory-upload-more"
        >
          <UploadCloud size={14} /> Upload more
        </button>
      </div>

      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center"
        aria-label="Close"
        data-testid="victory-dismiss"
      >
        ×
      </button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-black/30 border border-[var(--fop-border)] px-4 py-3 text-center">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
        {label}
      </div>
      <div className="font-display text-2xl font-bold text-[var(--fop-text)] mt-1 leading-none">
        {value}
      </div>
    </div>
  );
}
